<template>
  <section class="bg-white rounded-3xl border border-[#1c4848]/8 shadow-sm overflow-hidden">
    <header class="px-6 py-5 border-b border-[#1c4848]/5 flex items-center justify-between gap-4">
      <div>
        <h2 class="text-base font-bold text-[#1c4848]">AI modeli</h2>
        <p class="text-xs text-[#1c4848]/50 mt-0.5">
          Fajlovi žive na DigitalOcean Space-u (Media Library). Aplikacija čita verzije sa
          <code class="font-mono">/api/models/manifest</code> i nudi „Ažuriraj“ kad objaviš veću verziju.
        </p>
      </div>
      <button
        type="button"
        class="shrink-0 flex items-center gap-2 px-4 py-2.5 bg-[#1c4848] text-white rounded-xl shadow-lg shadow-[#1c4848]/20 hover:bg-[#153838] active:scale-95 transition-all text-xs font-bold"
        @click="openUpload(null, null)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
        Novi model
      </button>
    </header>

    <!-- Spatie Settings: the kill switch -->
    <div class="px-6 py-4 flex items-center justify-between gap-4 bg-[#fafbfc] border-b border-[#1c4848]/5">
      <div>
        <p class="text-sm font-semibold text-[#1c4848]">Preuzimanja uključena</p>
        <p class="text-xs text-[#1c4848]/50">Isključi dok mijenjaš fajlove — aplikacija prikazuje modele, ali ne preuzima.</p>
      </div>
      <button
        type="button"
        role="switch"
        :aria-checked="store.settings.enabled"
        :disabled="store.saving"
        class="relative w-12 h-7 rounded-full transition-colors"
        :class="store.settings.enabled ? 'bg-[#1c4848]' : 'bg-[#1c4848]/20'"
        @click="toggleEnabled"
      >
        <span class="absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all" :class="store.settings.enabled ? 'left-6' : 'left-1'"></span>
      </button>
    </div>

    <div v-if="store.loading && !store.list.length" class="p-6 space-y-3">
      <div v-for="i in 3" :key="i" class="h-16 rounded-2xl bg-[#1c4848]/5 animate-pulse"></div>
    </div>

    <table v-else class="w-full text-sm">
      <thead>
        <tr class="text-[10px] uppercase tracking-wider text-[#1c4848]/40 text-left">
          <th class="px-6 py-3 font-bold">Model</th>
          <th class="px-4 py-3 font-bold">Verzija</th>
          <th class="px-4 py-3 font-bold">Veličina</th>
          <th class="px-4 py-3 font-bold">sha256</th>
          <th class="px-4 py-3 font-bold">Objavljen</th>
          <th class="px-6 py-3 font-bold text-right">Akcije</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in store.rows" :key="row.key" class="border-t border-[#1c4848]/5">
          <td class="px-6 py-4">
            <p class="font-semibold text-[#1c4848]">{{ row.name }}</p>
            <p class="text-[11px] font-mono text-[#1c4848]/50">{{ row.key }}</p>
            <p v-if="row.hint" class="text-[11px] text-[#1c4848]/40 mt-0.5">{{ row.hint }}</p>
          </td>
          <td class="px-4 py-4 text-[#1c4848]">
            <span v-if="row.missing" class="text-[#1c4848]/40">—</span>
            <span v-else class="font-bold">v{{ row.version }}</span>
          </td>
          <td class="px-4 py-4 text-[#1c4848]/70">{{ row.missing ? '—' : `${row.size_mb} MB` }}</td>
          <td class="px-4 py-4 font-mono text-[11px] text-[#1c4848]/50" :title="row.sha256 || ''">
            {{ row.sha256 ? row.sha256.slice(0, 12) + '…' : '—' }}
          </td>
          <td class="px-4 py-4">
            <span v-if="row.missing" class="text-[11px] font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700">nije uploadovan</span>
            <button
              v-else
              type="button"
              class="text-[11px] font-bold px-2 py-1 rounded-full transition-colors"
              :class="row.is_published ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-[#1c4848]/5 text-[#1c4848]/60 hover:bg-[#1c4848]/10'"
              @click="togglePublished(row)"
            >
              {{ row.is_published ? 'objavljen' : 'sakriven' }}
            </button>
          </td>
          <td class="px-6 py-4 text-right whitespace-nowrap">
            <a
              v-if="row.file"
              :href="row.file"
              target="_blank"
              rel="noopener"
              class="text-xs font-bold text-[#1c4848]/60 hover:text-[#1c4848] mr-3"
            >Fajl ↗</a>
            <button
              type="button"
              class="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#1c4848]/5 text-[#1c4848] hover:bg-[#1c4848]/10"
              @click="openUpload(row.missing ? null : row, row.missing ? row : null)"
            >
              {{ row.missing ? 'Uploaduj' : 'Nova verzija' }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="store.error" class="px-6 py-3 text-xs text-red-600 border-t border-red-100 bg-red-50">{{ store.error }}</p>
  </section>
</template>

<script setup>
import { onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import { useAiModelsStore } from '@/store/ai-models/index.js';
import UploadAiModelModal from '@/components/app/dashboard/modals/UploadAiModelModal.vue';

const store = useAiModelsStore();

onMounted(() => { store.fetchAll().catch(() => {}); });

function openUpload(model, preset) {
  const { open, close } = useModal({
    component: UploadAiModelModal,
    attrs: {
      model,
      preset,
      onClose: () => close(),
      onSaved: () => store.fetchAll().catch(() => {}),
    },
  });
  open();
}

async function toggleEnabled() {
  try { await store.updateSettings({ enabled: !store.settings.enabled }); } catch {}
}

async function togglePublished(row) {
  try { await store.setPublished(row.key, !row.is_published); } catch {}
}
</script>
